import APIService from "@/common/API"
import { parsedUser } from "@/common/GetCurrentUser"
import { AgentActivityLogItem, AgentEmailHistoryItem } from "@/types/Agents.types"
import moment from "moment"
import { useCallback, useEffect, useState } from "react"
import { Badge, Card, Col, Row, Spinner } from "react-bootstrap"
import { useTranslation } from "react-i18next"

interface Props {
    agentId: string
    userId?: string | null
}

const QUANTITY = 8

const ACTION_BADGE_MAP: Record<string, string> = {
    Create: "success",
    Update: "warning",
    Delete: "danger",
    Login: "info",
}

// Keeps AgentDetailsPage informed by rendering nothing when both lists are empty, matching
// the same "hide the block" behaviour applied to the map and invoices sections.
const AgentActivity = ({ agentId, userId }: Props) => {
    const { t } = useTranslation()
    const user = parsedUser()

    const [logs, setLogs] = useState<AgentActivityLogItem[]>([])
    const [logsLoading, setLogsLoading] = useState(false)

    const [emails, setEmails] = useState<AgentEmailHistoryItem[]>([])
    const [emailsLoading, setEmailsLoading] = useState(false)

    const bindLogs = useCallback(async () => {
        if (!user?.OrganizationId || !userId) return
        try {
            setLogsLoading(true)
            const request = await APIService.getAction(
                "ActivityLog/List",
                `OrganizationId=${user.OrganizationId}&UserId=${userId}&Quantity=${QUANTITY}&Page=0`
            )
            if (request.data.status === "ok") {
                setLogs(request.data.response || [])
            }
        } catch (ex) {
            console.error(ex)
        } finally {
            setLogsLoading(false)
        }
    }, [user?.OrganizationId, userId])

    const bindEmails = useCallback(async () => {
        if (!user?.OrganizationId || !agentId) return
        try {
            setEmailsLoading(true)
            const request = await APIService.getAction(
                "agents/EmailHistory",
                `OrganizationId=${user.OrganizationId}&Id=${agentId}&Quantity=${QUANTITY}&Page=0`
            )
            if (request.data.status === "ok") {
                setEmails(request.data.response || [])
            }
        } catch (ex) {
            console.error(ex)
        } finally {
            setEmailsLoading(false)
        }
    }, [user?.OrganizationId, agentId])

    useEffect(() => {
        bindLogs()
    }, [bindLogs])

    useEffect(() => {
        bindEmails()
    }, [bindEmails])

    const loading = logsLoading || emailsLoading

    if (!loading && logs.length === 0 && emails.length === 0) return null

    return (
        <Card className="shadow-sm border-0 mb-4">
            <Card.Header className="bg-white border-bottom">
                <h6 className="mb-0">
                    <i className="fa fa-clock-rotate-left me-2 text-primary" />
                    {t("Recent Activity")}
                </h6>
            </Card.Header>
            <Card.Body>
                <Row>
                    <Col xs={12} lg={6} className="mb-3 mb-lg-0">
                        <h6 className="small text-muted text-uppercase mb-2">{t("Platform Usage")}</h6>
                        {logsLoading ? (
                            <div className="text-center py-3">
                                <Spinner animation="border" variant="primary" size="sm" />
                            </div>
                        ) : logs.length === 0 ? (
                            <div className="text-muted small">{t("No recorded activity")}</div>
                        ) : (
                            <ul className="list-unstyled mb-0">
                                {logs.map(log => (
                                    <li key={log.id} className="d-flex justify-content-between align-items-start py-2 border-bottom">
                                        <div>
                                            <div className="small">
                                                {log.action && (
                                                    <Badge bg={ACTION_BADGE_MAP[log.action] ?? "secondary"} className="me-2">
                                                        {log.action}
                                                    </Badge>
                                                )}
                                                <span className="text-muted">{log.section}</span>
                                            </div>
                                            {log.content && <div className="small text-muted">{log.content}</div>}
                                        </div>
                                        <small className="text-muted text-nowrap ms-2" title={moment(log.date).format("YYYY-MM-DD HH:mm:ss")}>
                                            {moment(log.date).fromNow()}
                                        </small>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Col>
                    <Col xs={12} lg={6}>
                        <h6 className="small text-muted text-uppercase mb-2">{t("Emails Sent")}</h6>
                        {emailsLoading ? (
                            <div className="text-center py-3">
                                <Spinner animation="border" variant="primary" size="sm" />
                            </div>
                        ) : emails.length === 0 ? (
                            <div className="text-muted small">{t("No emails sent")}</div>
                        ) : (
                            <ul className="list-unstyled mb-0">
                                {emails.map(email => (
                                    <li key={email.id} className="d-flex justify-content-between align-items-start py-2 border-bottom">
                                        <div className="small">{email.subject || t("(No subject)")}</div>
                                        <small className="text-muted text-nowrap ms-2" title={moment(email.date).format("YYYY-MM-DD HH:mm:ss")}>
                                            {moment(email.date).fromNow()}
                                        </small>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    )
}

export default AgentActivity
